import type { Update } from '@reduxjs/toolkit';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import type {
  Attribute,
  AttributeReferenceKey,
  Variant,
  VariantAttributesValues,
  VariantManagement,
} from '@polyai/schemas';
import {
  isNoCodeStepReferencesAttribute,
  isStepReferencesAttribute,
  isUpdateTopicReferencesAttribute,
} from '@polyai/stream/store/matchers';
import {
  createAttribute,
  createVariant,
  deleteAttribute,
  deleteVariant,
  setDefaultVariant,
  updateAttribute,
  updateVariant,
  variantImportVariants,
} from '@polyai/stream/store/reducers/variant/actions';
import {
  selectActiveAttributes,
  selectAttribute,
  selectAttributeByIds,
  selectVariant,
  selectVariantAttributeValues,
  selectVariantByIds,
  selectVariantManagement,
} from '@polyai/stream/store/reducers/variant/selectors';
import { createReferenceAdapter } from '@polyai/stream/store/references';
import { assignDefinedFields } from '@polyai/stream/store/utils/assign-fields';
import { assignMetadata } from '@polyai/stream/store/utils/assign-metadata';
import {
  handleCreateFlowReferences,
  handleCreateTopicReferences,
  handleDeleteFlowNoCodeStepReferences,
  handleDeleteFlowReferences,
  handleDeleteFlowStepReferences,
  handleDeleteNoCodeStepReferences,
  handleDeleteTopicReferences,
  handleImportFlowReferences,
  handleNoCodeStepReferenceUpdates,
  handleStepReferenceUpdates,
  handleTopicReferenceUpdates,
} from '@polyai/stream/store/utils/reference-helpers';

import {
  createFlow,
  deleteFlow,
  deleteFlowStep,
  deleteNoCodeStep,
  deleteStep,
  importFlow,
} from '../flows';
import { createTopic, deleteTopic } from '../knowledge-base';

const variantAdapter = createEntityAdapter<Variant>();
const variantSelectors = variantAdapter.getSelectors();

const attributeAdapter = createEntityAdapter<Attribute>();
const attributeSelectors = attributeAdapter.getSelectors();

const variantAttributeValuesAdapter =
  createEntityAdapter<VariantAttributesValues>();
const variantAttributeValuesSelectors =
  variantAttributeValuesAdapter.getSelectors();

const attributeReferences = createReferenceAdapter([
  'topics',
  'flowSteps',
  'noCodeSteps',
]);

const initialState = {
  variants: variantAdapter.getInitialState(),
  attributes: attributeAdapter.getInitialState(),
  variantAttributeValues: variantAttributeValuesAdapter.getInitialState(),
} satisfies VariantManagement;

const slice = createSlice({
  name: 'variant',
  initialState,
  reducers: {},
  // all reducers are here because actions are defined externally
  extraReducers: builder => {
    // main slice reducers
    builder.addCase(createVariant, (state, action) => {
      const { attributeValues, ...createVariant } = action.payload;
      const variant: Variant = {
        ...createVariant,
        name: createVariant.name.trim(),
        isDefault: Object.values(state.variants.entities).some(v => v.isDefault)
          ? false
          : true,
        createdAt: '',
        createdBy: '',
        updatedAt: '',
        updatedBy: '',
      };
      assignMetadata(action, variant);
      variantAdapter.addOne(state.variants, variant);

      // add the variant attribute values
      variantAttributeValuesAdapter.upsertOne(
        state.variantAttributeValues,
        assignDefinedFields(
          {
            id: variant.id,
            values: attributeValues.values,
          },
          { id: variant.id },
        ),
      );
    });

    builder.addCase(updateVariant, (state, action) => {
      const existingVariant = variantSelectors.selectById(
        state.variants,
        action.payload.id,
      );
      const variantAttributeValues = variantAttributeValuesSelectors.selectById(
        state.variantAttributeValues,
        action.payload.id,
      );

      const { attributeValues, ...updateVariant } = action.payload;

      const updatedVariant = assignDefinedFields(
        {
          ...existingVariant,
        },
        {
          ...updateVariant,
          name: updateVariant.name?.trim(),
        },
      );
      assignMetadata(action, {
        ...updatedVariant,
        updatedAt: undefined,
        updatedBy: undefined,
      });
      variantAdapter.upsertOne(state.variants, updatedVariant);

      // update the variant attribute values
      variantAttributeValuesAdapter.upsertOne(
        state.variantAttributeValues,
        assignDefinedFields(
          { ...variantAttributeValues, values: attributeValues?.values ?? {} },
          {
            id: action.payload.id,
          },
        ),
      );
    });

    builder.addCase(deleteVariant, (state, action) => {
      variantAdapter.removeOne(state.variants, action.payload.id);
      variantAttributeValuesAdapter.removeOne(
        state.variantAttributeValues,
        action.payload.id,
      );

      // Check that we still have a default variant
      const remainingVariants = Object.values(state.variants.entities);
      if (
        !remainingVariants.some(variant => variant.isDefault) &&
        remainingVariants.length
      ) {
        remainingVariants[0].isDefault = true;
      }
    });

    builder.addCase(createAttribute, (state, action) => {
      const { variantValues, ...createAttribute } = action.payload;

      const attribute: Attribute = {
        ...createAttribute,
        archived: false,
        createdAt: '',
        createdBy: '',
        updatedAt: '',
        updatedBy: '',
      };
      assignMetadata(action, attribute);
      attributeAdapter.addOne(state.attributes, attribute);

      // add the attribute to all variant attribute values
      // update the variant attribute values
      const updateValues: Update<VariantAttributesValues, string>[] =
        state.variantAttributeValues.ids.map(id => ({
          id,
          changes: {
            values: {
              ...state.variantAttributeValues.entities[id]?.values,
              [createAttribute.id]: variantValues?.values[id] || '',
            },
          },
        }));

      variantAttributeValuesAdapter.updateMany(
        state.variantAttributeValues,
        updateValues,
      );
    });

    builder.addCase(updateAttribute, (state, action) => {
      const existingAttribute = attributeSelectors.selectById(
        state.attributes,
        action.payload.id,
      );
      const { variantValues, ...updateAttribute } = action.payload;

      const updatedAttribute = assignDefinedFields(
        {
          ...existingAttribute,
        },
        updateAttribute,
      );
      assignMetadata(action, {
        ...updatedAttribute,
        updatedAt: undefined,
        updatedBy: undefined,
      });
      attributeAdapter.upsertOne(state.attributes, updatedAttribute);

      const updateValues: Update<VariantAttributesValues, string>[] =
        state.variantAttributeValues.ids.map(id => ({
          id,
          changes: {
            values: {
              ...state.variantAttributeValues.entities[id]?.values,
              [updateAttribute.id]: variantValues?.values[id] || '',
            },
          },
        }));

      variantAttributeValuesAdapter.updateMany(
        state.variantAttributeValues,
        updateValues,
      );
    });

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
    builder.addCase(variantImportVariants, (state, action) => {
      const {
        variants,
        attributes,
        variantAttributeValues,
        deletedVariantIds,
        deletedAttributeIds,
      } = action.payload;

      // upsert variants
      for (const variant of variants) {
        // Trim variant names to prevent trailing-space mismatches (PORT-4301)
        const trimmedVariant = { ...variant, name: variant.name.trim() };
        if (state.variants.entities[trimmedVariant.id]) {
          const updatedVariant = {
            ...trimmedVariant,
            updatedAt: '',
            updatedBy: '',
          };
          assignMetadata(action, updatedVariant);
          variantAdapter.updateOne(state.variants, {
            id: variant.id,
            changes: updatedVariant,
          });
        } else {
          const newVariant = {
            ...trimmedVariant,
            createdAt: '',
            createdBy: '',
            updatedAt: '',
            updatedBy: '',
          };
          assignMetadata(action, newVariant);
          variantAdapter.addOne(state.variants, newVariant);
        }
      }

      // upsert attribute
      for (const attribute of attributes) {
        if (state.attributes.entities[attribute.id]) {
          const updatedAttribute = {
            ...attribute,
            updatedAt: '',
            updatedBy: '',
          };
          assignMetadata(action, updatedAttribute);
          attributeAdapter.updateOne(state.attributes, {
            id: attribute.id,
            changes: updatedAttribute,
          });
        } else {
          const newAttribute = {
            ...attribute,
            archived: false,
            createdAt: '',
            createdBy: '',
            updatedAt: '',
            updatedBy: '',
          };
          assignMetadata(action, newAttribute);
          attributeAdapter.addOne(state.attributes, newAttribute);
        }
      }
      // delete variants
      for (const variantId of deletedVariantIds) {
        variantAdapter.removeOne(state.variants, variantId);
      }
      // delete attributes
      for (const attributeId of deletedAttributeIds) {
        attributeAdapter.removeOne(state.attributes, attributeId);
      }
      // delete variant attribute values
      for (const variantId of deletedVariantIds) {
        variantAttributeValuesAdapter.removeOne(
          state.variantAttributeValues,
          variantId,
        );
      }

      // delete attributes
      for (const attributeId of deletedAttributeIds) {
        for (const variantId of state.variantAttributeValues.ids) {
          const entity = state.variantAttributeValues.entities[variantId];
          const { [attributeId]: _, ...remainingValues } = entity?.values || {};
          variantAttributeValuesAdapter.updateOne(
            state.variantAttributeValues,
            {
              id: variantId,
              changes: { values: remainingValues },
            },
          );
        }
      }

      // upsert variant attribute values
      for (const variantAttributeValue of variantAttributeValues) {
        const existingVariantAttributeValue =
          state.variantAttributeValues.entities[
            variantAttributeValue.variantId
          ];
        if (existingVariantAttributeValue) {
          if (
            !deletedAttributeIds.includes(variantAttributeValue.attributeId)
          ) {
            delete existingVariantAttributeValue.values[
              variantAttributeValue.attributeId
            ];
          }
          variantAttributeValuesAdapter.updateOne(
            state.variantAttributeValues,
            {
              id: variantAttributeValue.variantId,
              changes: {
                values: {
                  ...existingVariantAttributeValue.values,
                  [variantAttributeValue.attributeId]:
                    variantAttributeValue.value,
                },
              },
            },
          );
        } else {
          variantAttributeValuesAdapter.addOne(state.variantAttributeValues, {
            id: variantAttributeValue.variantId,
            values: {
              [variantAttributeValue.attributeId]: variantAttributeValue.value,
            },
          });
        }
      }

      // Check that we still have a default variant
      const remainingVariants = Object.values(state.variants.entities);
      if (
        !remainingVariants.some(variant => variant.isDefault) &&
        remainingVariants.length
      ) {
        remainingVariants[0].isDefault = true;
      }
    });

    builder.addCase(deleteAttribute, (state, action) => {
      // Archive the attribute instead of removing it
      const existingAttribute = state.attributes.entities[action.payload.id];
      if (existingAttribute) {
        existingAttribute.archived = true;
        assignMetadata(action, existingAttribute);
      }

      // Still delete the variant attributes values for the attribute
      const deleteUpdates = state.variantAttributeValues.ids.map(id => {
        const entity = state.variantAttributeValues.entities[id];
        const { [action.payload.id]: _, ...remainingValues } =
          entity?.values || {};
        return {
          id,
          changes: {
            values: remainingValues,
          },
        };
      });
      variantAttributeValuesAdapter.updateMany(
        state.variantAttributeValues,
        deleteUpdates,
      );
    });
    builder.addCase(setDefaultVariant, (state, action) => {
      const variantUpdates: Update<Variant, string>[] = state.variants.ids.map(
        id => {
          const updatedVariant = {
            isDefault: id === action.payload.id,
            updatedAt: '',
            updatedBy: '',
          };
          assignMetadata(action, updatedVariant);
          return {
            id,
            changes: updatedVariant,
          };
        },
      );

      variantAdapter.updateMany(state.variants, variantUpdates);
    });

    // SIDE-EFFECT: FLOW
    builder.addCase(createFlow, (state, action) => {
      handleCreateFlowReferences(
        action.payload.steps,
        'attributes',
        action.payload.id,
        attributeReferences.addReference,
        state.attributes,
      );
    });

    builder.addCase(importFlow, (state, action) => {
      if (action.payload.attributes.length) {
        attributeAdapter.addMany(
          state.attributes,
          action.payload.attributes.map(a => {
            const attribute = {
              ...a,
              archived: false,
              createdAt: '',
              createdBy: '',
              updatedAt: '',
              updatedBy: '',
            };
            return assignMetadata(action, attribute);
          }),
        );
      }

      handleImportFlowReferences(
        action.payload.steps,
        'attributes',
        action.payload.flowId,
        attributeReferences.addReference,
        state.attributes,
      );
    });

    builder.addCase(deleteFlow, (state, action) => {
      handleDeleteFlowReferences(
        attributeSelectors.selectAll(state.attributes),
        action.payload.flowId,
        attributeReferences.removeReferencePrefix,
        state.attributes,
      );
      handleDeleteNoCodeStepReferences(
        attributeSelectors.selectAll(state.attributes),
        action.payload.flowId,
        attributeReferences.removeReferencePrefix,
        state.attributes,
      );
    });

    builder.addCase(deleteFlowStep, (state, action) => {
      handleDeleteFlowStepReferences(
        attributeSelectors.selectAll(state.attributes),
        action.payload.flowId,
        action.payload.stepId,
        attributeReferences.removeReference,
        state.attributes,
      );
    });

    builder.addCase(deleteNoCodeStep, (state, action) => {
      handleDeleteFlowNoCodeStepReferences(
        attributeSelectors.selectAll(state.attributes),
        action.payload.flowId,
        action.payload.stepId,

        attributeReferences.removeReference,
        state.attributes,
      );
    });

    builder.addCase(deleteStep, (state, action) => {
      handleDeleteFlowNoCodeStepReferences(
        attributeSelectors.selectAll(state.attributes),
        action.payload.flowId,
        action.payload.stepId,
        attributeReferences.removeReference,
        state.attributes,
      );
    });

    // SIDE-EFFECT: TOPIC
    builder.addCase(createTopic, (state, action) => {
      handleCreateTopicReferences<Attribute, AttributeReferenceKey>(
        state.attributes.entities,
        action.payload.references?.attributes ?? {},
        action.payload.id,
        attributeReferences.addReference,
        state.attributes,
      );
    });

    builder.addCase(deleteTopic, (state, action) => {
      handleDeleteTopicReferences<Attribute, AttributeReferenceKey>(
        state.attributes.entities,
        action.payload.id,
        attributeReferences.removeReference,
        state.attributes,
      );
    });

    // matchers must be after the case reducers

    builder.addMatcher(isUpdateTopicReferencesAttribute, (state, action) => {
      handleTopicReferenceUpdates(
        state.attributes.entities,
        action.payload.references?.attributes ?? {},
        action.payload.id,
        attributeReferences.addReference,
        attributeReferences.removeReference,
        state.attributes,
      );
    });

    builder.addMatcher(isNoCodeStepReferencesAttribute, (state, action) => {
      handleNoCodeStepReferenceUpdates(
        state.attributes.entities,
        action.payload.references?.attributes ?? {},
        action.payload.flowId,
        action.payload.stepId,
        attributeReferences.addReference,
        attributeReferences.removeReference,
        state.attributes,
      );
    });

    builder.addMatcher(isStepReferencesAttribute, (state, action) => {
      handleStepReferenceUpdates(
        state.attributes.entities,
        action.payload.step?.references?.attributes ?? {},
        action.payload.flowId,
        action.payload.step.id,
        attributeReferences.addReference,
        attributeReferences.removeReference,
        state.attributes,
      );
    });
  },
});

export {
  attributeAdapter,
  attributeReferences,
  variantAdapter,
  variantAttributeValuesAdapter,
};

export {
  attributeSelectors,
  variantAttributeValuesSelectors,
  variantSelectors,
};

export {
  createAttribute,
  createVariant,
  deleteAttribute,
  deleteVariant,
  setDefaultVariant,
  updateAttribute,
  updateVariant,
  variantImportVariants,
};
export {
  selectActiveAttributes,
  selectAttribute,
  selectAttributeByIds,
  selectVariant,
  selectVariantAttributeValues,
  selectVariantByIds,
  selectVariantManagement,
};
export type { Attribute, Variant, VariantAttributesValues, VariantManagement };
export default slice.reducer;
